import React, { useContext, useEffect, useState } from 'react'
import { NavLink as Link } from 'react-router-dom'
import classnames from 'classnames'
import { generatePath } from 'react-router'

import { APP_CONTAINER, ROUTES } from 'helpers/constants'
import config from 'config'
import { useAuthState, useBreakpoints } from 'hooks'
import { LayoutContext } from 'contexts/LayoutContext'
import { analytics } from 'helpers'

import {
  AuthModal,
  CloseIcon,
  HamburgerIcon,
  HeartIcon,
  MatchesIcon,
  OutsideAlerter,
  ProfileImage,
} from 'components'
import Dropdown from './Dropdown'

import teleportLogo from 'assets/images/logo-full.png'
import AVATAR from 'assets/icons/profile_placeholder.png'

const ACTIVE_WHITE_TEXT = 'text-white'
const ICON_CLASS = `${ACTIVE_WHITE_TEXT} h-5`
const AUTH_BUTTON_CLASS =
  'w-full h-14 px-2 text-sm rounded-lg border-none outline-none flex items-center justify-center hover:opacity-90 focus:outline-none md:h-10 md:w-32'

const { LANDING, LIKES, MATCHES } = ROUTES

const LogoIcon = () => <img src={teleportLogo} className="h-5 md:h-6" alt="logo" />

const NavLink = ({ className, icon, children, ...rest }) => (
  <Link
    activeClassName="opacity-100"
    className={classnames(
      'text-white inline-flex items-center opacity-50 hover:opacity-100',
      className
    )}
    {...rest}
  >
    {icon}
    <span className="ml-3">{children}</span>
  </Link>
)

const Text = props => <p className="secondary-text text-white hover:text-white" {...props} />

const Header = () => {
  const { user } = useAuthState()
  const { md } = useBreakpoints()
  const { state } = useContext(LayoutContext)
  const [showDropDown, setShowDropDown] = useState(false)
  const [showMobileNav, setShowMobileNav] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [hasUserScrolled, setHasUserScrolled] = useState(false)

  const hideDropdown = () => setShowDropDown(false)
  const hideAuthModal = () => setShowAuthModal(false)

  const isHeaderTransparent = !hasUserScrolled && state.isHeaderTransparent

  useEffect(() => {
    const onScroll = () => setHasUserScrolled(window.scrollY > 120)

    window.addEventListener('scroll', onScroll)

    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const updateBodyScroll = showMobileNav => {
    document.body.classList.toggle('overflow-y-hidden', showMobileNav)
    document.body.classList.toggle('h-screen', showMobileNav)
  }

  const toggleMobileNav = () => {
    setShowMobileNav(!showMobileNav)
    updateBodyScroll(!showMobileNav)
  }

  const hideMobileNav = () => {
    if (!md) {
      setShowMobileNav(false)
      updateBodyScroll(false)
    }
  }

  const GuestActions = () => (
    <div
      className={classnames('flex flex-1 flex-grow-1 justify-end', {
        'flex-col items-center flex-wrap pt-10 h-full ': !md,
      })}
    >
      <button
        onClick={() => {
          analytics.segmentTrack('Anonymous Clicked Sign In Button')
          setShowAuthModal(true)
        }}
        className={classnames('bg-white text-black hover:text-black', AUTH_BUTTON_CLASS)}
        data-cy="sign-in-button"
      >
        Sign in
      </button>
    </div>
  )

  return (
    <div className={classnames({ 'pt-20 md:pt-24': !isHeaderTransparent })}>
      <div
        className={classnames('fixed top-0 left-0 w-full z-10', {
          'z-20': !md && showMobileNav,
        })}
      >
        <header
          className={classnames(
            'relative w-full flex px-2 py-7 justify-between md:py-6 md:px-10 md:items-center',
            APP_CONTAINER.WIDTH_CLASS,
            {
              'md:bg-background': !isHeaderTransparent,
              'w-full px-5 items-center': !md,
              'bg-black fixed w-full h-screen wrap flex-wrap content-start overflow-y-scroll':
                !md && showMobileNav,
              'bg-background': !md && !showMobileNav && !isHeaderTransparent,
            }
          )}
        >
          <Link onClick={hideMobileNav} to={LANDING} className="flex items-center">
            <LogoIcon />
          </Link>
          {user && md && (
            <div className="space-x-10 flex flex-1 flex-grow-1 md-items-center justify-end">
              {!config.waitlist && (
                <>
                  <NavLink to={generatePath(LIKES)} icon={<HeartIcon className={ICON_CLASS} />}>
                    <Text>Likes</Text>
                  </NavLink>
                  <NavLink to={MATCHES} icon={<MatchesIcon className={ICON_CLASS} />}>
                    <Text>Matches</Text>
                  </NavLink>
                </>
              )}
              <OutsideAlerter clickedOutside={hideDropdown}>
                <button
                  onClick={() => {
                    setShowDropDown(!showDropDown)
                  }}
                  data-cy="profile-dropdown-btn"
                  className="hidden md:flex flex-row items-center cursor-pointer bg-transparent outline-none"
                >
                  {!user.profileImage && (
                    <div
                      className="flex items-center justify-center w-12 h-12 m-0 object-cover rounded-full"
                      style={{ background: '#141414' }}
                    >
                      <img src={AVATAR} alt="profile placeholder" className="w-7 h-7" />
                    </div>
                  )}
                  {user.profileImage && (
                    <ProfileImage
                      className="w-12 h-12 m-0 rounded-full object-cover"
                      src={user.profileImage}
                    />
                  )}

                  {showDropDown && <Dropdown />}
                </button>
              </OutsideAlerter>
            </div>
          )}
          {!md && (
            <div className="flex items-center">
              <button
                className="bg-transparent w-8 flex justify-end focus:outline-none"
                onClick={toggleMobileNav}
              >
                {showMobileNav ? (
                  <CloseIcon className={`${ACTIVE_WHITE_TEXT} w-4 h-6`} />
                ) : (
                  <HamburgerIcon className={`${ACTIVE_WHITE_TEXT} w-6 h-6`} />
                )}
              </button>
            </div>
          )}
          {!md && showMobileNav && (
            <div className="w-full mt-12">
              {user ? <Dropdown hideMobileNav={hideMobileNav} /> : <GuestActions />}
            </div>
          )}
          {!user && md && <GuestActions />}
        </header>
        <AuthModal
          show={showAuthModal}
          onClose={hideAuthModal}
          onComplete={() => {
            hideAuthModal()
            hideMobileNav()
          }}
        />
      </div>
    </div>
  )
}

export default Header
